import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiPage, ApiResource, Paginated } from "@/lib/api/types"
import type { Requirements } from "@/types/requirement"
import type {
  ApiFirm,
  ApiFirmListItem,
  CreateFirmInput,
  Firm,
  FirmFilters,
  FirmGroup,
  FirmListItem,
  UpdateFirmInput,
} from "@/types/firm"

const listPageSize = 100
const exportPageSize = 100

export type FirmListFilters = FirmFilters

export function toFirmListItem(data: ApiFirmListItem): FirmListItem {
  return {
    id: data.id,
    legalName: data.legal_name,
    frn: data.frn,
    companyNumber: data.company_number,
    kind: data.kind,
    kindLabel: data.kind_label,
    status: data.status,
    statusLabel: data.status_label,
    statusTone: data.status_tone,
    onboardingStageLabel: data.onboarding_stage_label,
    risk: data.risk,
    complianceScore: data.compliance_score,
    compliance: data.compliance,
    appointedLabel: data.appointed_label,
    group: data.group ?? null,
  }
}

export function toFirm(data: ApiFirm): Firm {
  return {
    id: data.id,
    legalName: data.legal_name,
    frn: data.frn,
    companyNumber: data.company_number,
    kind: data.kind,
    kindLabel: data.kind_label,
    status: data.status,
    statusLabel: data.status_label,
    statusTone: data.status_tone,
    onboardingStageLabel: data.onboarding_stage_label,
    risk: data.risk,
    complianceScore: data.compliance_score,
    compliance: data.compliance,
    appointedLabel: data.appointed_label,
    tradingNames: data.trading_names,
    legalForm: data.legal_form,
    legalFormLabel: data.legal_form_label,
    incorporatedOn: data.incorporated_on,
    appointedOn: data.appointed_on,
    endedOn: data.ended_on,
    changes: data.changes,
    notice: data.notice,
    overview: data.overview,
    nextSteps: data.next_steps,
    editNotice: data.edit_notice,
    statuses: data.statuses,
    unmet: data.unmet,
    people: data.people,
    can: {
      importRegisterPeople: data.can.import_register_people,
      sendRequest: data.can.send_request,
      chase: data.can.chase,
    },
    email: data.email,
    phone: data.phone,
    financialYearEnd:
      data.financial_year_end_day !== null &&
      data.financial_year_end_month !== null
        ? {
            day: data.financial_year_end_day,
            month: data.financial_year_end_month,
          }
        : null,
    nextFinancialYearEnd: data.next_financial_year_end,
    businessActivities: data.business_activities,
  }
}

export function toFirmPayload(
  input: CreateFirmInput | UpdateFirmInput | Record<string, unknown>
): Record<string, unknown> {
  const names: Record<string, string> = {
    legalName: "legal_name",
    kind: "kind",
    tradingNames: "trading_names",
    companyNumber: "company_number",
    legalForm: "legal_form",
    frn: "frn",
    incorporatedOn: "incorporated_on",
    email: "email",
    phone: "phone",
    appointedOn: "appointed_on",
    alreadyAppointed: "already_appointed",
  }
  const payload: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(input)) {
    if (key === "financialYearEnd") {
      const yearEnd = value as UpdateFirmInput["financialYearEnd"]
      payload.financial_year_end_day = yearEnd?.day ?? null
      payload.financial_year_end_month = yearEnd?.month ?? null
    } else if (names[key] !== undefined && value !== undefined) {
      payload[names[key]] = value
    }
  }

  return payload
}

function listParams(filters: FirmFilters, page: number, perPage: number) {
  const sorts = {
    legalName: "legal_name",
    risk: "risk",
    complianceScore: "compliance_score",
    appointedOn: "appointed_on",
    createdAt: "created_at",
  } as const

  return {
    search: filters.q,
    status: filters.status,
    kind: filters.kind,
    risk_reason: filters.risk_reason,
    risk_tier: filters.risk_tier,
    gap: filters.gap,
    sort: filters.sort ? sorts[filters.sort] : undefined,
    order: filters.order,
    group: filters.group,
    page,
    per_page: perPage,
  }
}

export type FirmPage = Paginated<FirmListItem> & {
  groups: FirmGroup[]
}

async function fetchFirmPage(
  filters: FirmFilters,
  page: number,
  perPage: number,
  signal?: AbortSignal
): Promise<FirmPage> {
  const response = await apiFetch<
    ApiPage<ApiFirmListItem> & { meta: { groups?: FirmGroup[] } }
  >("/firms", {
    params: listParams(filters, page, perPage),
    signal,
  })

  return {
    items: response.data.map(toFirmListItem),
    total: response.meta.total,
    currentPage: response.meta.current_page,
    lastPage: response.meta.last_page,
    groups: response.meta.groups ?? [],
  }
}

export const firmQueries = {
  list: (filters: FirmFilters = {}) =>
    queryOptions({
      queryKey: apiKey("/firms", filters),
      queryFn: async ({ signal }) =>
        await fetchFirmPage(filters, 1, listPageSize, signal),
    }),

  infiniteList: (filters: FirmListFilters = {}) =>
    infiniteQueryOptions({
      queryKey: [...apiKey("/firms", filters), "infinite"],
      queryFn: async ({ pageParam, signal }) =>
        await fetchFirmPage(filters, pageParam, listPageSize, signal),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.currentPage < lastPage.lastPage
          ? lastPage.currentPage + 1
          : undefined,
    }),

  detail: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiFirm>>(
          `/firms/${firmId}`,
          { signal }
        )

        return toFirm(data)
      },
    }),

  requirements: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}/requirements`),
      queryFn: async ({ signal }) => {
        return await apiFetch<Requirements>(`/firms/${firmId}/requirements`, {
          signal,
        })
      },
    }),
}

export async function loadAllFirms(
  filters: FirmListFilters = {},
  signal?: AbortSignal
): Promise<FirmListItem[]> {
  const firms: FirmListItem[] = []

  for (let page = 1; ; page += 1) {
    const result = await fetchFirmPage(filters, page, exportPageSize, signal)
    firms.push(...result.items)

    if (page >= result.lastPage) {
      return firms
    }
  }
}

export function useCreateFirm() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateFirmInput) => {
      const { data } = await apiFetch<ApiResource<ApiFirm>>("/firms", {
        method: "POST",
        body: toFirmPayload(input),
      })

      return toFirm(data)
    },
    onSuccess: (firm) => {
      queryClient.setQueryData(apiKey(`/firms/${firm.id}`), firm)
    },
  })
}

export function useUpdateFirm(firmId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateFirmInput | Record<string, unknown>) => {
      const { data } = await apiFetch<ApiResource<ApiFirm>>(
        `/firms/${firmId}`,
        { method: "PATCH", body: toFirmPayload(input) }
      )

      return toFirm(data)
    },
    onSuccess: (firm) => {
      queryClient.setQueryData(apiKey(`/firms/${firmId}`), firm)
    },
  })
}

export function useSetFirmAppointment(firmId: string) {
  return useUpdateFirm(firmId)
}

export function useArchiveFirm(firmId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { reason: string }) => {
      const { data } = await apiFetch<ApiResource<ApiFirm>>(
        `/firms/${firmId}/archive`,
        { method: "POST", body: { reason: input.reason.trim() || null } }
      )

      return toFirm(data)
    },
    onSuccess: (firm) => {
      queryClient.setQueryData(apiKey(`/firms/${firmId}`), firm)
    },
  })
}

export function useOffboardFirm(firmId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { reason: string }) => {
      const { data } = await apiFetch<ApiResource<ApiFirm>>(
        `/firms/${firmId}/offboarding`,
        { method: "POST", body: { reason: input.reason.trim() || null } }
      )

      return toFirm(data)
    },
    onSuccess: (firm) => {
      queryClient.setQueryData(apiKey(`/firms/${firmId}`), firm)
    },
  })
}

export function useForceFirm(
  firmId: string,
  change: "force_terminate" | "force_activate"
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      on: string
      reason: string
      confirmation?: string
    }) => {
      const { data } = await apiFetch<ApiResource<ApiFirm>>(
        `/firms/${firmId}/${change === "force_terminate" ? "force-terminate" : "force-activate"}`,
        { method: "POST", body: input }
      )

      return toFirm(data)
    },
    onSuccess: (firm) => {
      queryClient.setQueryData(apiKey(`/firms/${firmId}`), firm)
    },
  })
}
